import { motion } from "framer-motion";
import { Phone, MessageCircle, MapPin, Navigation } from "lucide-react";
import {
  PHONE_TEL,
  PHONE_DISPLAY,
  WA_DEFAULT,
  ADDRESS,
  MAPS_DIRECTIONS,
  MAPS_EMBED,
} from "@/data/content";

const reveal = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

export default function Contact() {
  return (
    <section id="contact" className="relative overflow-hidden bg-forest-deep px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
      <div
        className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full opacity-20"
        style={{ background: "radial-gradient(circle, #D4AF37 0%, transparent 70%)" }}
      />
      <div className="relative mx-auto grid max-w-7xl gap-12 lg:grid-cols-2 lg:items-center">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
        >
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold-light">
            Reserve Your Stay · <span className="font-kannada normal-case tracking-normal">ಬುಕ್ ಮಾಡಿ</span>
          </p>
          <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-amber-50 sm:text-4xl lg:text-4xl">
            One call, <span className="italic text-gold-gradient">and the room is yours</span>
          </h2>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-amber-100/75 sm:text-base">
            No forms, no waiting. Ring us or drop a WhatsApp message — tell us your dates
            and we'll keep a room ready, whatever hour you arrive.
          </p>

          <a
            href={PHONE_TEL}
            data-testid="quick-contact-phone-link"
            className="group mt-8 flex items-center gap-5 rounded-lg border border-gold/30 bg-forest-dark/60 p-5 transition-all duration-300 hover:border-gold/60 hover:bg-forest-dark"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gold text-forest-dark transition-transform duration-300 group-hover:scale-110">
              <Phone className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-[10px] font-bold uppercase tracking-[0.3em] text-amber-100/60">
                Call the front desk
              </span>
              <span className="block font-sans text-xl font-bold tabular-nums tracking-wide text-gold-light sm:text-2xl">
                {PHONE_DISPLAY}
              </span>
            </span>
          </a>

          <div className="mt-5 flex flex-wrap gap-4">
            <a
              href={WA_DEFAULT}
              target="_blank"
              rel="noreferrer"
              data-testid="quick-contact-whatsapp-link"
              className="inline-flex items-center gap-3 rounded-full bg-gold px-6 py-3.5 text-xs font-bold uppercase tracking-widest text-forest-dark transition-all duration-300 hover:scale-[1.04] hover:bg-gold-light"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp Us
            </a>
            <a
              href={MAPS_DIRECTIONS}
              target="_blank"
              rel="noreferrer"
              data-testid="google-maps-direction-button"
              className="inline-flex items-center gap-3 rounded-full border border-gold/50 px-6 py-3.5 text-xs font-bold uppercase tracking-widest text-gold-light transition-all duration-300 hover:bg-gold/15"
            >
              <Navigation className="h-4 w-4" />
              Get Directions
            </a>
          </div>

          <div className="mt-8 flex items-start gap-3 text-sm text-amber-100/80">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
            <p data-testid="contact-address">
              <span className="font-semibold text-amber-50">Hotel Anil Palace</span>
              <br />
              {ADDRESS}
              <br />
              <span className="text-xs text-amber-100/60">
                Between Kundapura & Byndoor · easy halt from Mangalore, Udupi & Kerala routes
              </span>
            </p>
          </div>
        </motion.div>

        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="overflow-hidden rounded-lg gold-frame"
        >
          <iframe
            title="Hotel Anil Palace on Google Maps"
            data-testid="google-maps-iframe"
            src={MAPS_EMBED}
            className="h-[380px] w-full border-0 sm:h-[460px]"
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
          />
        </motion.div>
      </div>
    </section>
  );
}
